import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_processes_list } from "../../api";
import type { ContextualAsk } from "../../chat/AskContext";
import type { AskAction } from "../../chat/askStarters";
import type { FilterOption } from "../FilterMultiSelect";
import { useKeyedRequest } from "../../utils/useKeyedRequest";
import { AppSelect } from "../AppSelect";
import type { ProcessAction } from "../processes/processAction";

/** Actions configure navigation; they never send a chat message. */
export function AskProcessActionSetup({
  target,
  kind,
  userOptions,
  processOptions,
  onCancel,
  onOpen,
}: {
  target: ContextualAsk;
  kind: Exclude<AskAction, "kpi">;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  onCancel: () => void;
  onOpen: (slug: string, action: ProcessAction) => void;
}) {
  const { t } = useTranslation();
  const scopedPeople = userOptions.filter(
    user => !target.filters.userIds || target.filters.userIds.includes(user.id),
  );
  const peopleLabel =
    scopedPeople.length === userOptions.length
      ? t("watchlist.all_people")
      : scopedPeople.length === 1
        ? scopedPeople[0].label
        : t("dashboard.people_selected", { count: scopedPeople.length });
  const { from, to } = target.filters;
  // The directory takes inclusive bounds; Ask's range ends at an exclusive instant, and starts
  // at the epoch when it covers all time.
  const datetimeGte = from && new Date(from).getTime() !== 0 ? from : undefined;
  const datetimeLte = to ? new Date(new Date(to).getTime() - 1).toISOString() : undefined;
  const datesLabel =
    datetimeGte && datetimeLte
      ? `${format(new Date(datetimeGte), "d MMM yyyy")} – ${format(new Date(datetimeLte), "d MMM yyyy")}`
      : t("dashboard.all_time");
  const scopeLabel = kind === "explore" ? target.label : `${peopleLabel} (${datesLabel})`;
  const scopeKey = JSON.stringify({
    user_ids: target.filters.userIds,
    datetime_gte: datetimeGte,
    datetime_lte: datetimeLte,
  });
  const processList = useKeyedRequest(kind === "processes" ? scopeKey : null, async () => {
    const query: Parameters<typeof get_processes_list>[0] = JSON.parse(scopeKey);
    return (await get_processes_list(query))
      .filter(process => process.frequency > 0)
      .map(process => ({ id: process.slug, label: process.name }));
  });
  const { loading, failed } = processList;
  const availableProcesses = (
    kind === "processes" ? (processList.data ?? []) : processOptions
  ).filter(
    option =>
      kind !== "explore" ||
      (target.sessionProcesses?.includes(option.label) &&
        (!target.filters.processes || target.filters.processes.includes(option.label))),
  );
  const selectedProcesses = processOptions.filter(option =>
    target.filters.processes?.includes(option.label),
  );
  const [processId, setProcessId] = useState(
    selectedProcesses[0]?.id ?? (availableProcesses.length === 1 ? availableProcesses[0].id : ""),
  );
  const [first, setFirst] = useState(selectedProcesses[0]?.id ?? "");
  const [second, setSecond] = useState(selectedProcesses[1]?.id ?? "");
  const loadedOptions = processList.data;
  useEffect(() => {
    if (!loadedOptions) return;
    setFirst(current => (loadedOptions.some(option => option.id === current) ? current : ""));
    setSecond(current => (loadedOptions.some(option => option.id === current) ? current : ""));
  }, [loadedOptions]);
  const a = availableProcesses.find(option => option.id === first);
  const b = availableProcesses.find(option => option.id === second);
  const process = availableProcesses.find(option => option.id === processId);
  const valid = kind === "explore" ? !!process : !!a && !!b && first !== second;
  const choose = (
    label: string,
    value: string,
    onChange: (id: string) => void,
    choices: FilterOption[],
    excluded?: string,
  ) => (
    <div className="space-y-2">
      <p className="font-medium text-text">{label}</p>
      <AppSelect
        label={label}
        disabled={loading || failed || choices.length === 0}
        value={choices.some(option => option.id === value) ? value : ""}
        onChange={onChange}
        placeholder={t("askPanel.choose_item")}
        choices={choices.map(option => ({
          id: option.id,
          display: option.label,
          disabled: option.id === excluded,
        }))}
      />
    </div>
  );
  return (
    <section className="h-full overflow-y-auto p-4 text-sm">
      <h3 className="text-lg font-semibold text-text">
        {t(kind === "explore" ? "askPanel.explore_process" : "askPanel.compare_processes")}
      </h3>
      <p className="mt-2 leading-relaxed text-text-mid">
        {t(kind === "explore" ? "askPanel.explore_help" : "askPanel.compare_help")}
      </p>
      <p className="mt-3 text-xs text-text-mid">
        {scopeLabel.replace(/ · (.*)/, " ($1)").replaceAll(" · ", ", ")}
      </p>
      {kind !== "explore" && (
        <p className="mt-2 text-xs text-text-dim">{t("askPanel.compare_dates")}</p>
      )}
      <div className="my-5 space-y-4">
        {kind !== "processes" &&
          choose(t("askPanel.choose_process"), processId, setProcessId, availableProcesses)}
        {kind !== "explore" && (
          <>
            {choose(t("askPanel.choose_first"), first, setFirst, availableProcesses, second)}
            {choose(t("askPanel.choose_second"), second, setSecond, availableProcesses, first)}
          </>
        )}
      </div>
      {loading ? (
        <p role="status" className="text-text-mid">
          {t("loading")}
        </p>
      ) : failed ? (
        <div role="alert" className="space-y-2 text-text-mid">
          <p>{t("askPanel.compare_load_error")}</p>
          <button type="button" className="text-primary" onClick={() => processList.reload()}>
            {t("retry")}
          </button>
        </div>
      ) : kind === "processes" && availableProcesses.length < 2 ? (
        <p className="text-text-mid">{t("askPanel.compare_not_enough")}</p>
      ) : (
        !availableProcesses.length && <p className="text-text-mid">{t("askPanel.no_processes")}</p>
      )}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-outline px-3 py-2"
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          disabled={!valid}
          className="rounded-lg bg-info px-3 py-2 font-medium text-on-accent disabled:opacity-40"
          onClick={() => {
            if (!valid) return;
            if (kind === "explore" && process)
              onOpen(process.id, { kind: "session", filters: target.filters });
            else if (a && b)
              onOpen(a.id, { kind: "processes", secondSlug: b.id, filters: target.filters });
          }}
        >
          {t(kind === "explore" ? "askPanel.open_process" : "askPanel.compare_start")}
        </button>
      </div>
    </section>
  );
}
